import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

/**
 * getTriggerOpts - Returns the ScrollTrigger options that will trigger the video to play.
 * @param {HTMLVideoElement} video - The video element to play.
 * @param {boolean} isDesktop - Whether the device is desktop or not.
 * @param {boolean} isRM - Whether the user prefers reduced motion or not.
 * @returns {Object} Ther trigger options.
 */
const getTriggerOpts = (video, isDesktop, isRM) => {
  return {
    start: isDesktop ? 'top center+=30%' : 'center bottom',
    trigger: video,
    once: true,
    onEnter: () => {
      video.style.opacity = 1;
      if (!isRM) {
        video.play();
      } else {
        video.currentTime = video.duration;
      }
    }
  };
};

/**
 * positionTip - Positions the pin content tip.
 * @param {HTMLDivElement} pin - The pin element.
 * @param {HTMLDivElement} content - The pin content element.
 * @param {HTMLDivElement} container - The pin container element.
 * @returns {void}
 */
const positionTip = (pin, content, container) => {
  let top;
  let left;
  const xOffset = pin.clientWidth / 2;
  top = ((pin.offsetTop - 35) / container.clientHeight) * 100;
  left = ((pin.offsetLeft + xOffset) / container.clientWidth) * 100;
  content.style.top = `${top}%`;
  content.style.left = `${left}%`;
};

const closePin = (pin) => {
  if (!pin) return;
  const pinTitle = pin.querySelector('[data-systems-pin-title]');
  pinTitle.innerHTML = pinTitle.getAttribute('data-saved-title');
  pin.setAttribute('aria-expanded', false);
  const content = document.getElementById(pin.getAttribute('aria-controls'));
  content.classList.remove('selected');
};

const init = (isRM) => {
  let pinRevealStarted = false;
  let itemSelected = false;
  let pinRevealTL;
  const container = document.querySelector('[data-systems-pin-container]');
  if (!container) return;
  const video = container.querySelector('[data-systems-video]');
  const pins = container.querySelectorAll('[data-systems-pin]');

  if (!container || !video || !pins) {
    return;
  }

  const mm = gsap.matchMedia();
  mm.add(
    {
      isDesktop: '(min-width: 1181px)'
    },
    (context) => {
      const { isDesktop } = context.conditions;

      if (isDesktop) {
        // Play the video when it enters the viewport.
        ScrollTrigger.create(getTriggerOpts(video, isDesktop, isRM));

        context.add('onItemClose', (event) => {
          const pin = event.currentTarget;
          if (pin === itemSelected) {
            closePin(pin);
            itemSelected = false;
          }
        });

        // handle pin selection and show the content
        context.add('onItemSelect', (event) => {
          if (event.stopImmediatePropagation) event.stopImmediatePropagation();

          const pin = event.currentTarget;
          if (pin !== itemSelected) {
            // A new pin was selected.
            if (itemSelected) {
              // There was a previous pin selected so we should close it.
              closePin(itemSelected);
            }
            // Open the new pin
            pin.setAttribute('aria-expanded', true);
            const pinTitle = pin.querySelector('[data-systems-pin-title]');
            pinTitle.innerHTML = 'Close tooltip';
            const content = document.getElementById(
              pin.getAttribute('aria-controls')
            );

            positionTip(pin, content, container);

            content.classList.add('selected');
            itemSelected = pin;
            pin.addEventListener('click', context.onItemClose);
          }
        });

        // Create the pin reveal timeline and assign pin event handlers.
        pinRevealTL = gsap.timeline({ paused: true });
        pins.forEach((pin) => {
          pinRevealTL.add(() => {
            const pTL = gsap.timeline();

            pTL.to(pin.querySelector('[data-systems-pin-line]'), {
              scaleY: 1,
              duration: 0.3
            });

            pTL.to(pin.querySelector('[data-systems-pin-circle]'), {
              opacity: 1,
              scale: 1,
              duration: 0.3
            });

            return pTL;
          }, '+=0.1');

          pin.addEventListener('pointerover', context.onItemSelect);
        });

        context.add('onVideTimeUpdate', () => {
          // Trigger the pin reveal animation when the video reaches 2 seconds.
          if (video.currentTime > 2 && !pinRevealStarted) {
            pinRevealStarted = true;
            pinRevealTL.play();
          }

          // Select the first pin when the video reaches 2.7 seconds.
          if (video.currentTime > 2.7 && !itemSelected) {
            context.onItemSelect({ currentTarget: pins[0] });
          }
        });
        video.addEventListener('timeupdate', context.onVideTimeUpdate);
      }

      // GSAP matchMedia cleanup.
      return () => {
        if (!isDesktop) return;

        closePin(itemSelected);
        pinRevealStarted = false;
        itemSelected = false;
        pins.forEach((pin) => {
          pin.removeEventListener('pointerover', context.onItemSelect);
          pin.removeEventListener('click', context.onItemClose);
          video.removeEventListener('timeupdate', context.onVideTimeUpdate);
        });
      };
    }
  );
};

export { init };
